import { extractUnitOptions } from '#/modules/imsMgmt/helper/materialUnit/extractUnitOptions';
import { pickDefaultUnitId } from '#/modules/imsMgmt/helper/materialUnit/pickDefaultUnitId';
import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import { maxIssueQtyInUnit } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueQtyBounds';

export { maxIssueQtyInUnit as maxReturnQtyInUnit };

/** 明細來源 id：一般庫存 invStockId，或待分配 pendingAllocationId（二擇一）。 */
export function getReturnLineSourceId(line) {
  return line?.pendingAllocationId || line?.invStockId || '';
}

/**
 * 雙欄操作 state → create／update body（camel；API 層再 serializeStockReturnWrite）。
 * Line：returnedQty ← draft qty；可選 unitId（有值才帶）。
 * 來源：待分配列送 pendingAllocationId，其餘送 invStockId（二擇一）。
 */
export function buildStockReturnWritePayload({
  returnDate,
  sourceWarehouseId,
  targetWarehouseId,
  targetLocationId = null,
  issueReason = null,
  remark = null,
  draftLines = [],
}, { updatedAt = null } = {}) {
  const payload = {
    returnDate: returnDate || null,
    sourceWarehouseId: sourceWarehouseId || null,
    targetWarehouseId: targetWarehouseId || null,
    targetLocationId: targetLocationId || null,
    issueReason: issueReason || null,
    remark: remark || null,
    lines: (draftLines ?? []).map((line) => {
      const linePayload = line?.pendingAllocationId
        ? { pendingAllocationId: line.pendingAllocationId }
        : { invStockId: line?.invStockId ?? null };
      linePayload.returnedQty = Number(line?.qty);
      linePayload.remark = line?.remark ?? null;
      const unitId = String(line?.unitId ?? '').trim();
      if (unitId) {
        linePayload.unitId = unitId;
      }
      return linePayload;
    }).filter((line) => (
      (line.invStockId || line.pendingAllocationId)
      && Number.isFinite(line.returnedQty)
      && line.returnedQty >= 0
    )),
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

/** @deprecated 請改用 {@link buildStockReturnWritePayload} */
export function mapReturnDraftToCreatePayload(options) {
  return buildStockReturnWritePayload(options);
}

function formatWarehouseLocationLabel(warehouse) {
  if (!warehouse) {
    return '';
  }
  const warehouseLabel = [warehouse.code, warehouse.name].filter(Boolean).join(' ')
    || warehouse.name
    || '';
  const location = warehouse.location;
  if (!location) {
    return warehouseLabel;
  }
  const locationLabel = [location.code, location.name].filter(Boolean).join(' ')
    || location.name
    || '';
  return [warehouseLabel, locationLabel].filter(Boolean).join(' / ');
}

/**
 * 建列（picker／scan）：mtrId、空 unitId、availableQty＝base；options 可由 UI hook 補。
 * 預設 qty＝floor(availableBase / rate(defaultUnit))（無 options 時＝全退 base）。
 */
export function createReturnDraftLine({
  mtrId = '',
  mtrNo = '',
  mtrName = '',
  invStockId = '',
  pendingAllocationId = '',
  stockNo = '',
  availableQty = 0,
  fromWarehouseLabel = '',
  remark = null,
  unitOptions = [],
  unitId = '',
  qty = null,
} = {}) {
  if (!invStockId && !pendingAllocationId) {
    return null;
  }

  const availableBase = Number(availableQty);
  const safeAvailable = Number.isFinite(availableBase) && availableBase > 0
    ? availableBase
    : 0;
  const options = Array.isArray(unitOptions) ? unitOptions : [];
  const defaultUnitId = String(unitId ?? '').trim()
    || pickDefaultUnitId(options)
    || '';
  const maxQty = maxIssueQtyInUnit(safeAvailable, options, defaultUnitId);
  const resolvedQty = qty != null && qty !== ''
    ? Number(qty)
    : (maxQty > 0 ? maxQty : 0);

  if (!Number.isFinite(resolvedQty) || resolvedQty <= 0) {
    return null;
  }

  return {
    mtrId,
    mtrNo,
    mtrName,
    invStockId: pendingAllocationId ? '' : invStockId,
    pendingAllocationId,
    stockNo,
    availableQty: safeAvailable,
    unitId: '',
    unitOptions: options,
    qty: resolvedQty,
    fromWarehouseLabel,
    remark,
  };
}

/**
 * detail → 雙欄操作 state（header + draftLines）。
 * 輸入視角：優先 `qty.returnedQty`＋`unit.id`；相容 `inputQty`／`inputUnitId`。
 * **勿**用 `baseQty.*`／`baseUnit` 回填表單（庫存視角另欄）。
 * availableQty＝庫存 base（勿用 line 退料量充當）。`returnedQty === 0` 仍保留。
 */
function resolveReturnedInputQty(line) {
  const raw = line?.qty?.returnedQty ?? line?.returnedQty ?? line?.inputQty;
  if (raw == null || raw === '') {
    return null;
  }
  const qty = Number(raw);
  return Number.isFinite(qty) ? qty : null;
}

export function mapStockReturnDetailToOperateState(detail) {
  const lines = detail?.lines ?? [];

  return {
    returnDate: detail?.returnDate ?? '',
    sourceWarehouseId: detail?.sourceWarehouse?.id ?? detail?.sourceWarehouseId ?? '',
    targetWarehouseId: detail?.targetWarehouse?.id ?? detail?.targetWarehouseId ?? '',
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    updatedAt: detail?.updatedAt ?? detail?.record?.updatedAt ?? null,
    draftLines: lines.map((line) => {
      const qty = resolveReturnedInputQty(line);
      const unitId = resolveUnitId(line.unit)
        || String(line.inputUnitId ?? '').trim()
        || '';
      const unitOptions = extractUnitOptions(line.mtr ?? line);
      const warehouse = line.fromWarehouse
        ?? line.sourceWarehouse
        ?? line.invStock?.warehouse
        ?? detail?.sourceWarehouse;
      const availableQty = Number(
        line.invStock?.availableQty
        ?? line.invStock?.baseQty
        ?? line.availableQty
        ?? 0,
      );

      // 讀回 pendingAllocationId＝該列採待分配來源；此時 invStock 是核准後實際收到的庫存項，不是來源。
      const pendingAllocationId = line.pendingAllocationId ?? '';

      return {
        mtrId: line.mtr?.id ?? '',
        mtrNo: line.mtr?.mtrNo ?? '',
        mtrName: line.mtr?.name ?? '',
        invStockId: pendingAllocationId
          ? ''
          : (line.invStock?.id ?? line.invStockId ?? ''),
        pendingAllocationId,
        stockNo: line.invStock?.stockNo ?? line.stockNo ?? '',
        qty,
        availableQty: Number.isFinite(availableQty) && availableQty > 0
          ? availableQty
          : 0,
        unitId,
        unitOptions,
        fromWarehouseLabel: formatWarehouseLocationLabel(warehouse),
        remark: line.remark ?? null,
      };
    }).filter((line) => (
      getReturnLineSourceId(line) && line.qty != null && line.qty >= 0
    )),
  };
}
